import type { DashboardEditOperation } from "@t3tools/contracts";
import { applyEdits } from "@t3tools/shared/dashboardEdits";

import type { ArtifactSpec, ArtifactWidget } from "./artifactTypes";

/**
 * What a person can do to one widget without asking the assistant. Each choice
 * becomes the same edit operations the assistant uses, so it saves as a version
 * and undoes the same way. A choice is offered only when the operations apply
 * and leave a valid dashboard.
 */
export type DirectDisplay = "line" | "area" | "bar" | "stacked_bar" | "table";

export type DirectEdit =
  | { kind: "rename"; title: string }
  | { kind: "view_as"; display: DirectDisplay }
  | { kind: "move"; direction: "earlier" | "later" }
  | { kind: "width"; span: number | null }
  | { kind: "remove" };

export const DISPLAY_LABELS: Record<DirectDisplay, string> = {
  area: "Area chart",
  bar: "Bar chart",
  line: "Line chart",
  stacked_bar: "Stacked bar chart",
  table: "Table",
};

export const WIDTH_CHOICES: ReadonlyArray<{ label: string; span: number | null }> = [
  { label: "Full width", span: 12 },
  { label: "Half width", span: 6 },
  { label: "One third", span: 4 },
  { label: "Share the row equally", span: null },
];

type Place = { row: number; index: number };

function placeOf(spec: ArtifactSpec, widgetId: string): Place | null {
  for (const [row, entry] of spec.rows.entries()) {
    const index = entry.widgets.findIndex((widget) => widget.id === widgetId);
    if (index !== -1) return { index, row };
  }
  return null;
}

function metricQueryOf(widget: ArtifactWidget) {
  return widget.kind !== "text" && widget.data.kind === "metric" ? widget.data.query : null;
}

/** The widget as another display, or null when its data can't be shown that way. */
function asDisplay(widget: ArtifactWidget, display: DirectDisplay): ArtifactWidget | null {
  if (widget.kind === "text" || widget.kind === "kpi") return null;
  const query = metricQueryOf(widget);
  const base = { id: widget.id, title: widget.title };
  if (display === "table") {
    if (widget.kind === "table" || !query) return null;
    const columns = [
      ...(query.grain === "total" ? [] : [{ column: "period", label: "Period" }]),
      ...query.dimensions.map((id) => ({ column: id, label: id.replaceAll("_", " ") })),
      { column: "value", label: widget.title },
    ];
    return { ...base, columns, data: widget.data, kind: "table" } as ArtifactWidget;
  }
  if (widget.kind === "chart") return { ...widget, chart: display };
  if (!query) return null;
  const x = query.grain === "total" ? query.dimensions[0] : "period";
  if (!x) return null;
  return {
    ...base,
    chart: display,
    data: widget.data,
    kind: "chart",
    ...(query.grain !== "total" && query.dimensions[0] ? { groupBy: query.dimensions[0] } : {}),
    series: [{ column: "value", label: widget.title }],
    x,
  } as ArtifactWidget;
}

function replaceWidget(
  spec: ArtifactSpec,
  place: Place,
  next: ArtifactWidget,
): DashboardEditOperation[] {
  const row = spec.rows[place.row]!;
  const alone = row.widgets.length === 1;
  return [
    { op: "remove_widget", widgetId: next.id },
    {
      at: { index: alone ? 0 : place.index, row: place.row },
      op: "add_widget",
      widget: next,
      ...(alone ? { newRow: row.title ? { title: row.title } : {} } : {}),
    } as DashboardEditOperation,
  ];
}

/** The operations for an edit, or null when it has no operations here. */
export function directEditOps(
  spec: ArtifactSpec,
  widgetId: string,
  edit: DirectEdit,
): DashboardEditOperation[] | null {
  const place = placeOf(spec, widgetId);
  if (!place) return null;
  const widget = spec.rows[place.row]!.widgets[place.index]!;
  switch (edit.kind) {
    case "rename": {
      const title = edit.title.trim();
      return title && title !== widget.title
        ? [{ op: "update_widget", set: { title }, widgetId }]
        : null;
    }
    case "remove":
      return [{ op: "remove_widget", widgetId }];
    case "width":
      return [{ op: "resize_widget", span: edit.span, widgetId }];
    case "view_as": {
      if (widget.kind === "chart" && edit.display !== "table") {
        return widget.chart === edit.display
          ? null
          : [{ op: "update_widget", set: { chart: edit.display }, widgetId }];
      }
      const next = asDisplay(widget, edit.display);
      return next ? replaceWidget(spec, place, next) : null;
    }
    case "move": {
      const rows = spec.rows;
      if (edit.direction === "earlier") {
        if (place.index > 0) {
          return [{ op: "move_widget", to: { index: place.index - 1, row: place.row }, widgetId }];
        }
        if (place.row === 0) return null;
        const previous = rows[place.row - 1]!;
        return [
          {
            op: "move_widget",
            to: { index: previous.widgets.length, row: place.row - 1 },
            widgetId,
          },
        ];
      }
      const row = rows[place.row]!;
      if (place.index < row.widgets.length - 1) {
        return [{ op: "move_widget", to: { index: place.index + 1, row: place.row }, widgetId }];
      }
      if (place.row === rows.length - 1) return null;
      // Taking the last widget out of a one-widget row deletes the row, so the next row shifts up.
      const target = row.widgets.length === 1 ? place.row : place.row + 1;
      return [{ op: "move_widget", to: { index: 0, row: target }, widgetId }];
    }
  }
}

/** The edit's operations when they apply and leave a valid dashboard, otherwise null. */
export function validDirectEditOps(
  spec: ArtifactSpec,
  widgetId: string,
  edit: DirectEdit,
): DashboardEditOperation[] | null {
  const ops = directEditOps(spec, widgetId, edit);
  if (!ops) return null;
  return "issues" in applyEdits(spec, ops) ? null : ops;
}

/** Stacking only means something with a split or more than one series. */
function stacks(widget: ArtifactWidget): boolean {
  return widget.kind === "chart" && (widget.groupBy !== undefined || widget.series.length > 1);
}

export type WidgetChoices = {
  viewAs: DirectDisplay[];
  canMoveEarlier: boolean;
  canMoveLater: boolean;
  widths: ReadonlyArray<{ label: string; span: number | null }>;
  canRemove: boolean;
};

/** What the menu for one widget offers. */
export function widgetChoices(spec: ArtifactSpec, widgetId: string): WidgetChoices {
  const place = placeOf(spec, widgetId);
  const widget = place ? spec.rows[place.row]!.widgets[place.index]! : null;
  const displays: DirectDisplay[] = ["line", "area", "bar", "stacked_bar", "table"];
  const viewAs =
    widget && widget.kind !== "text" && widget.kind !== "kpi"
      ? displays.filter(
          (display) =>
            (display !== "stacked_bar" || stacks(widget)) &&
            validDirectEditOps(spec, widgetId, { display, kind: "view_as" }) !== null,
        )
      : [];
  const alone = place ? spec.rows[place.row]!.widgets.length === 1 : true;
  return {
    canMoveEarlier:
      validDirectEditOps(spec, widgetId, { direction: "earlier", kind: "move" }) !== null,
    canMoveLater: validDirectEditOps(spec, widgetId, { direction: "later", kind: "move" }) !== null,
    canRemove: place !== null,
    viewAs,
    widths: alone ? [] : WIDTH_CHOICES,
  };
}

/** The operation for a new dashboard title, or null for an empty or unchanged one. */
export function renameDashboardOps(
  spec: ArtifactSpec,
  title: string,
): DashboardEditOperation[] | null {
  const next = title.trim();
  return next && next !== spec.title ? [{ op: "set_title", title: next }] : null;
}
